import { createFeatureSelector, createSelector, select } from '@ngrx/store'
import { pipe } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { parseMatchToLineupDiagram } from '@pages/match-detail/utils/match-detail.utils'
import { selectLiveMatches, selectTopic } from '@core/store/selectors/socket.selectors'
import { buildMatchTrends, getMatchIdByLocation } from '@core/utils/match.utils'
import { Topic } from '@sockets/enums/socket.enum'
import { MatchDetailState } from '@pages/match-detail/store/states/match-detail.state'
import { parseSpiderChartDatasource } from '@shared/components/spider-chart/utils/spider-chart.utils'
import { Match, MatchVideoAvailable } from '@core/models/match.models'
import { MATCH_DETAIL_FEATURE_TOKEN } from '@pages/match-detail/constants/match-detail.constants'
import { buildMatchDualHeader } from '@pages/match-detail/utils/match-details-dual-header.utils'
import {
  selectAggregationsLeftAggregation,
  selectAggregationsModeList,
  selectAggregationsRightAggregation
} from '@features/aggregations/store/selectors/aggregations.selectors'
import {
  AGGREGATIONS_AVERAGE_SELECTABLE_MODE,
  AGGREGATIONS_MATCH_SELECTABLE_MODE
} from '@features/aggregations/constants/aggregation.constants'
import { selectSeasonsByContext } from '@features/seasons/state/selectors/seasons.selectors'
import { findSeasonAndCompetitionByID } from '@core/utils/season.utils'
import { CallingContext } from '@features/seasons/models/seasons.models'
import { AggregationAllowed } from '@features/aggregations/models/aggregations.models'

export const selectMatchDetailFeature = (state: any) => state.matchDetail

export const selectMatch = createSelector(selectMatchDetailFeature, (state) => state?.match)

export const selectMatchMetrics = createSelector(selectMatchDetailFeature, (state) => state?.metrics)
export const selectMatchPreviousMetricMap = createSelector(selectMatchDetailFeature, (state) => state?.previousMetrics)
export const selectMatchMetricsOverview = createSelector(selectMatchMetrics, (metricMap) => metricMap?.metricsOverview)
export const selectMatchMetricCategories = createSelector(
  selectMatchMetrics,
  (metricMap) => metricMap?.metricCategories
)
export const selectMatchPreviousMetrics = createSelector(
  selectMatchPreviousMetricMap,
  (metricMap) => metricMap?.metricsPrevious
)
export const selectMatchChartMetrics = createSelector(
  selectMatchPreviousMetricMap,
  (metricMap) => metricMap?.chartMetricResults
)

export const selectMatchLastMatches = createSelector(selectMatchDetailFeature, (state) => state?.lastMatches)
export const selectMatchTrends = createSelector(selectMatch, selectMatchLastMatches, (match, lastMatches) => {
  if (match && lastMatches) {
    return buildMatchTrends(match, lastMatches)
  }
})

export const selectMatchSpiderChart = createSelector(selectMatchPreviousMetricMap, (metrics) =>
  parseSpiderChartDatasource(metrics)
)

export const selectMatchLineup = createSelector(selectMatchDetailFeature, (state) => state.lineup)
export const selectMatchLineupLoader = createSelector(selectMatchDetailFeature, (state) => state.lineupLoading)

export const selectMatchLineupFieldDiagram = createSelector(selectMatch, (match) => parseMatchToLineupDiagram(match))

export const selectHeatMap = createSelector(selectMatchDetailFeature, (state) => state.heatMap)
export const selectHeatMapLoader = createSelector(selectMatchDetailFeature, (state) => state.heatMapLoading)

export const selectPassMatrix = createSelector(selectMatchDetailFeature, (state) => state.passMatrix)
export const selectPassMatrixLoader = createSelector(selectMatchDetailFeature, (state) => state.passMatrixLoading)

export const selectEffectiveTime = createSelector(selectMatchMetrics, (metrics) => metrics?.metricsEffectiveTime)
export const selectLiveMatch = (callingContext: CallingContext) =>
  pipe(
    select(createSelector(selectLiveMatches(callingContext), (streaming) => streaming)),
    map((streaming) => (streaming || []).find((m) => m.id === getMatchIdByLocation())),
    filter((updateMatch) => !!updateMatch)
  )

export const selectMatchChanged = () =>
  pipe(
    select(createSelector(selectTopic(Topic.MatchChanged), (match: Match) => match)),
    filter((match) => !!match && match.id === getMatchIdByLocation())
  )

export const selectNewVideoAvailable = () =>
  pipe(
    select(
      createSelector(selectTopic(Topic.NewVideoAvailable), (videoAvailable: MatchVideoAvailable) => videoAvailable)
    ),
    filter((videoAvailable) => !!videoAvailable && videoAvailable.matchId === getMatchIdByLocation())
  )

export const selectMatchHighLights = createSelector(selectMatchDetailFeature, (state: MatchDetailState) => {
  return state?.assets?.find((asset) => asset.qualifierContentType === 'Highlights')?.url
})

export const selectMatchLoader = createSelector(selectMatchDetailFeature, (state) => state.matchLoading)
export const selectMatchPreviousMetricsLoader = createSelector(
  selectMatchDetailFeature,
  (state) => state.prevMetricLoading
)
export const selectMatchMetricsLoader = createSelector(selectMatchDetailFeature, (state) => state.metricsLoading)
export const selectMatchTrendsLoader = createSelector(selectMatchDetailFeature, (state) => state.trendLoading)

export const selectMatchTabs = createSelector(selectMatchDetailFeature, (state) => state.tabs)
export const selectMatchActiveTabId = createSelector(selectMatchDetailFeature, (state) => state.activeTabId)

export const selectMatchPreviousLeftAggregation = createSelector(
  selectMatchDetailFeature,
  (state) => state.previousLeftAggregation
)

export const selectMatchPreviousRightAggregation = createSelector(
  selectMatchDetailFeature,
  (state) => state.previousRightAggregation
)

export const selectMatchPreviousAggregations = createSelector(
  selectMatchPreviousLeftAggregation,
  selectMatchPreviousRightAggregation,
  (leftAggregation, rightAggregation) => ({
    leftAggregation,
    rightAggregation
  })
)

const selectLeftTeam = createSelector(selectMatchDetailFeature, (state) => state.leftTeam)
const selectRightTeam = createSelector(selectMatchDetailFeature, (state) => state.rightTeam)

const selectPreviousLeftTeam = createSelector(selectMatchDetailFeature, (state) => state.previousLeftTeam)
const selectPreviousRightTeam = createSelector(selectMatchDetailFeature, (state) => state.previousRightTeam)

export const selectMatchLeftTeam = createSelector(selectMatch, selectLeftTeam, (match, leftTeam) => {
  if (match) {
    return leftTeam ?? match.homeTeam
  }
})

export const selectMatchRightTeam = createSelector(selectMatch, selectRightTeam, (match, rightTeam) => {
  if (match) {
    return rightTeam ?? match.awayTeam
  }
})

export const selectMatchPreviousLeftTeam = createSelector(selectMatch, selectPreviousLeftTeam, (match, leftTeam) => {
  if (match) {
    return leftTeam ?? match.homeTeam
  }
})

export const selectMatchPreviousRightTeam = createSelector(selectMatch, selectPreviousRightTeam, (match, rightTeam) => {
  if (match) {
    return rightTeam ?? match.awayTeam
  }
})

export const selectLineupAggregation = createSelector(
  selectMatchDetailFeature,
  (state) => state.lineupAggregation ?? AGGREGATIONS_AVERAGE_SELECTABLE_MODE
)

export const selectMatchDualHeader = createSelector(
  selectMatch,
  selectSeasonsByContext('MatchesSpace'),
  selectMatchLeftTeam,
  selectMatchRightTeam,
  selectAggregationsLeftAggregation,
  selectAggregationsRightAggregation,
  (match, seasons, leftTeam, rightTeam, leftAggregation, rightAggregation) => {
    if (match && leftTeam && rightTeam && leftAggregation && rightAggregation && seasons?.length) {
      return buildMatchDualHeader(match, seasons, leftTeam, rightTeam, leftAggregation, rightAggregation)
    }
  }
)
export const selectMatchPreviousTabDualHeader = createSelector(
  selectMatch,
  selectSeasonsByContext('MatchesSpace'),
  selectMatchPreviousLeftTeam,
  selectMatchPreviousRightTeam,
  selectMatchPreviousLeftAggregation,
  selectMatchPreviousRightAggregation,
  (match, seasons, leftTeam, rightTeam, leftAggregation, rightAggregation) => {
    if (match && leftTeam && rightTeam && leftAggregation && rightAggregation && seasons?.length) {
      return buildMatchDualHeader(match, seasons, leftTeam, rightTeam, leftAggregation, rightAggregation)
    }
  }
)

export const selectMatchPreviousTabSpiderChartDualHeader = createSelector(
  selectMatch,
  selectSeasonsByContext('MatchesSpace'),
  (match, seasons) => {
    if (match && seasons?.length) {
      return buildMatchDualHeader(
        match,
        seasons,
        match.homeTeam,
        match.awayTeam,
        AGGREGATIONS_AVERAGE_SELECTABLE_MODE,
        AGGREGATIONS_AVERAGE_SELECTABLE_MODE
      )
    }
  }
)

export const selectMatchLineupAggregation = createSelector(
  selectMatchDetailFeature,
  (state: MatchDetailState) => state.lineupAggregation ?? AGGREGATIONS_MATCH_SELECTABLE_MODE
)

export const selectSeasonAndCompetitionByIds = (seasonId: string, competitionId: string) =>
  createSelector(selectSeasonsByContext('MatchesSpace'), (seasons) => {
    if (seasons?.length) {
      return findSeasonAndCompetitionByID(seasons, seasonId, competitionId)
    }
  })

export const selectHasPreviousTabAccess = createSelector(
  selectMatch,
  selectSeasonsByContext('MatchesSpace'),
  (match, seasons) => {
    if (match && seasons?.length) {
      return findSeasonAndCompetitionByID(seasons, match.seasonId, match.competitionId)?.competition
        ?.hasFullCompetitionPermission
    }
  }
)

export const selectMatchLeftTeamAggregationModes = createSelector(
  selectMatchLeftTeam,
  selectAggregationsModeList,
  (team, aggregationModes) => {
    if (team && aggregationModes) {
      const aggregationAllowed = Object.keys(team['aggregationDataSourceAllowed'] as AggregationAllowed)
      return aggregationModes.filter((mode) => aggregationAllowed.includes(mode.id))
    }
  }
)

export const selectMatchRightTeamAggregationModes = createSelector(
  selectMatchRightTeam,
  selectAggregationsModeList,
  (team, aggregationModes) => {
    if (team && aggregationModes) {
      const aggregationAllowed = Object.keys(team['aggregationDataSourceAllowed'] as AggregationAllowed)
      return aggregationModes.filter((mode) => aggregationAllowed.includes(mode.id))
    }
  }
)
